var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { format } from "date-fns";
import { maxStudyAdditionalSections } from "healthex-common/constants";
import { maxAiToolNameLengthForAiDataUseDefaults } from "healthex-common/project.data.utils";
import { GlobalAdminProjectListStatus, } from "healthex-common/global-admin-projects.dtos";
import { isErr } from "healthex-common/result";
import { ProjectType, StudyVersionStatus } from "healthex-common/study.dtos";
import { UserType } from "healthex-common/user.dtos";
import { autorun, computed, observable, runInAction } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { validators } from "../../Logic/Validators";
import { FormModel } from "../FormModel";
import { SimpleDialogModel } from "../SimpleDialogModel";
import { catalogFieldExceedsCharacterLimit, catalogFieldMissing, contactMethodRequiredMessage, firstValidationErrorPage, catalogPagesForForm, emptyGlobalAdminProjectFormInput, globalAdminProjectFormInputFromStudy, isContactMethodChoiceFieldName, isStringFormField, toCreateStudyInput, toUpdateStudyVersionInput, } from "./GlobalAdminProjectFormInput";
export const PROJECT_BASICS_PAGE = "PROJECT_BASICS";
export const PROJECT_TEAM_MEMBERS_PAGE = "PROJECT_TEAM_MEMBERS";
export class GlobalAdminProjectsViewModel {
    constructor(container) {
        this.loadGeneration = 0;
        this.ownersLoadGeneration = 0;
        this.formLoadGeneration = 0;
        this.deleteDraftConfirmDialog = new SimpleDialogModel();
        this.publishConfirmDialogOpen = false;
        this.publishConfirmed = false;
        this.publishVersionName = "";
        this.formOpen = false;
        this.formPage = PROJECT_BASICS_PAGE;
        this.projects = NOT_LOADED;
        this.projectOwners = [];
        this.projectOwnersLoadFailed = false;
        this.projectsLoadFailed = false;
        this.listError = null;
        this.formMode = "create";
        this.editingProjectId = null;
        this.editingVersionName = null;
        this.editingProjectPublished = false;
        this.editingProjectHasDraft = false;
        this.formLoading = false;
        this.formBusy = false;
        this.formSuccessMessage = null;
        this.globalAdminUIManager = container.globalAdminUIManager;
        this.globalAdminProjectsManager = container.globalAdminProjectsManager;
        this.organizationsManager = container.organizationsManager;
        this.provisioningService = container.provisioningService;
        this.formModel = new FormModel(emptyGlobalAdminProjectFormInput(""), ["title"], () => {
            this.formSuccessMessage = null;
        });
        window.setTimeout(() => {
            autorun(() => {
                void this.loadProjects(this.globalAdminUIManager.organizationIdToAdmin);
                void this.loadProjectOwners(this.globalAdminUIManager.organizationIdToAdmin);
            });
        }, 0);
    }
    get isAIProject() {
        return this.formModel.input.projectType === ProjectType.AI_DATA_USE;
    }
    get isPdda() {
        return this.globalAdminUIManager.organizationTypeToAdmin === UserType.EXTERNAL_RESEARCHER;
    }
    get editingProjectName() {
        return this.formModel.input.title.trim();
    }
    get adminOrganizationId() {
        return this.globalAdminUIManager.organizationIdToAdmin;
    }
    get catalogPages() {
        return catalogPagesForForm(this.formModel.input);
    }
    get publishVersionNameVisible() {
        return this.editingProjectPublished;
    }
    get publishContinueEnabled() {
        return this.publishConfirmed && this.publishVersionName.trim() !== "";
    }
    get currentCatalogPage() {
        var _a;
        return (_a = this.catalogPages.find((page) => page.pageName === this.formPage)) !== null && _a !== void 0 ? _a : null;
    }
    showNewProject() {
        var _a, _b;
        this.formLoadGeneration += 1;
        this.resetForm();
        this.formMode = "create";
        this.editingProjectId = null;
        this.editingVersionName = null;
        this.listError = null;
        this.formModel.updateInput({ ownerId: (_b = (_a = this.projectOwners[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "", projectType: ProjectType.AI_DATA_USE });
        this.formOpen = true;
    }
    closeProjectForm() {
        this.formLoadGeneration += 1;
        this.formOpen = false;
        this.resetForm();
    }
    setFormPage(page) {
        this.formPage = page;
    }
    get additionalQuestionLimitReached() {
        return this.formModel.input.additionalQuestions.length >= maxStudyAdditionalSections;
    }
    addAdditionalQuestion() {
        if (this.additionalQuestionLimitReached) {
            return;
        }
        this.formModel.updateInput({
            additionalQuestions: [...this.formModel.input.additionalQuestions, { id: null, title: "", description: "" }],
        });
    }
    updateAdditionalQuestion(index, changes) {
        this.formModel.updateInput({
            additionalQuestions: this.formModel.input.additionalQuestions.map((question, questionIndex) => questionIndex === index ? { ...question, ...changes } : question),
        });
    }
    removeAdditionalQuestion(index) {
        const question = this.formModel.input.additionalQuestions[index];
        if (question === undefined) {
            return;
        }
        this.formModel.updateInput({
            additionalQuestions: this.formModel.input.additionalQuestions.filter((_item, questionIndex) => questionIndex !== index),
            removedAdditionalQuestionIds: question.id === null
                ? this.formModel.input.removedAdditionalQuestionIds
                : [...this.formModel.input.removedAdditionalQuestionIds, question.id],
        });
    }
    async openProject(project) {
        await this.openProjectForEditing(project.id, project.status === GlobalAdminProjectListStatus.PUBLISHED);
    }
    async openProjectForEditing(projectId, published) {
        var _a;
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        if (!organizationId) {
            return;
        }
        const generation = ++this.formLoadGeneration;
        this.resetForm();
        this.formMode = "edit";
        this.editingProjectPublished = published;
        this.formLoading = true;
        this.formOpen = true;
        this.listError = null;
        try {
            const loaded = await this.loadStudyForEditing(organizationId, projectId, published);
            if (generation !== this.formLoadGeneration) {
                return;
            }
            if (!loaded || loaded.study.projectType !== ProjectType.AI_DATA_USE) {
                this.closeProjectForm();
                this.listError = "Unable to load project";
                return;
            }
            this.editingProjectId = projectId;
            this.editingProjectHasDraft = loaded.isDraft;
            this.editingVersionName = (_a = loaded.study.versionName) !== null && _a !== void 0 ? _a : null;
            this.formModel.updateInput(globalAdminProjectFormInputFromStudy(loaded.study, this.targetOrganizationName()));
        }
        catch {
            if (generation !== this.formLoadGeneration) {
                return;
            }
            this.closeProjectForm();
            this.listError = "Unable to load project";
        }
        finally {
            if (generation === this.formLoadGeneration) {
                this.formLoading = false;
            }
        }
    }
    async loadStudyForEditing(organizationId, projectId, published) {
        const draft = await this.globalAdminProjectsManager.getProject(organizationId, projectId, StudyVersionStatus.DRAFT);
        if (draft) {
            return { study: draft, isDraft: true };
        }
        if (published) {
            const activeStudy = await this.globalAdminProjectsManager.getProject(organizationId, projectId, StudyVersionStatus.ACTIVE);
            return activeStudy ? { study: activeStudy, isDraft: false } : null;
        }
        return null;
    }
    async setOrganizationIcon(file) {
        await this.uploadOrganizationImage("organizationIconUrl", file);
    }
    async setOrganizationRectangularIcon(file) {
        await this.uploadOrganizationImage("organizationRectangularIconUrl", file);
    }
    async uploadOrganizationImage(field, file) {
        if (file === null) {
            this.formModel.updateInput({ [field]: "" });
            this.formModel.clearError(field);
            return;
        }
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        const projectId = this.editingProjectId;
        if (!organizationId || !projectId) {
            this.formModel.addError(field, "Unable to upload image");
            return;
        }
        this.formBusy = true;
        try {
            if (this.editingProjectPublished) {
                await this.globalAdminProjectsManager.createProjectDraftIfNecessary(organizationId, projectId);
            }
            const uploadInfo = field === "organizationIconUrl"
                ? await this.globalAdminProjectsManager.getOrgIconUrl(organizationId, projectId, file)
                : await this.globalAdminProjectsManager.getOrgRectangularIconUrl(organizationId, projectId, file);
            if (!uploadInfo.fileUrl) {
                this.formModel.addError(field, "Unable to upload image");
                return;
            }
            await this.provisioningService.uploadFileToUrl(uploadInfo.signedUrl, file);
            this.formModel.updateInput({ [field]: uploadInfo.fileUrl });
            this.formModel.clearError(field);
        }
        catch {
            this.formModel.addError(field, "Unable to upload image");
        }
        finally {
            this.formBusy = false;
        }
    }
    async createProject() {
        if (!this.validateInput(false)) {
            return;
        }
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        const ownerId = this.formModel.input.ownerId;
        if (!organizationId || !ownerId) {
            this.formModel.formError = "Unable to save project";
            return;
        }
        this.formBusy = true;
        try {
            const projectId = await this.globalAdminProjectsManager.createProject(organizationId, toCreateStudyInput(this.formModel.input, ownerId, this.isPdda));
            if (!projectId) {
                this.formModel.formError = "Unable to create project";
                return;
            }
            await this.reloadProjects();
            await this.openProjectForEditing(projectId, false);
        }
        catch {
            this.formModel.formError = "Unable to create project";
        }
        finally {
            this.formBusy = false;
        }
    }
    async saveDraft() {
        await this.submit(false);
    }
    async publish() {
        if (!this.validateInput(true)) {
            return;
        }
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        const projectId = this.editingProjectId;
        if (!organizationId || !projectId) {
            this.formModel.formError = "Unable to publish project";
            return;
        }
        try {
            const versionNumber = await this.globalAdminProjectsManager.getProjectDraftVersionNumber(organizationId, projectId);
            this.publishVersionName = `Version ${versionNumber} (${format(new Date(), "LLL dd, yyyy")})`;
        }
        catch {
            this.formModel.formError = "Unable to publish project";
            return;
        }
        this.publishConfirmed = false;
        this.publishConfirmDialogOpen = true;
    }
    closePublishConfirmDialog() {
        this.publishConfirmDialogOpen = false;
        this.publishConfirmed = false;
    }
    async confirmPublish() {
        if (!this.publishContinueEnabled) {
            return;
        }
        this.publishConfirmDialogOpen = false;
        await this.submit(true, this.publishVersionName.trim());
        this.publishConfirmed = false;
    }
    async confirmDeleteDraft() {
        const confirmed = await this.deleteDraftConfirmDialog.showAndWait();
        this.deleteDraftConfirmDialog.open = false;
        if (!confirmed) {
            return;
        }
        await this.deleteDraft();
    }
    async deleteDraft() {
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        const projectId = this.editingProjectId;
        if (!organizationId || !projectId) {
            return;
        }
        this.formBusy = true;
        try {
            await this.globalAdminProjectsManager.deleteProjectDraft(organizationId, projectId);
            this.closeProjectForm();
            await this.reloadProjects();
        }
        catch {
            this.formModel.formError = "Unable to delete draft";
        }
        finally {
            this.formBusy = false;
        }
    }
    async submit(publish, publishVersionName) {
        if (!this.validateInput(publish)) {
            return;
        }
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        const projectId = this.editingProjectId;
        if (!organizationId || !projectId) {
            this.formModel.formError = "Unable to save project";
            return;
        }
        this.formBusy = true;
        try {
            if (this.editingProjectPublished) {
                await this.globalAdminProjectsManager.createProjectDraftIfNecessary(organizationId, projectId);
            }
            const saved = await this.updateDraft(organizationId, projectId, publish, publishVersionName !== null && publishVersionName !== void 0 ? publishVersionName : null);
            if (!saved) {
                return;
            }
            if (publish) {
                this.closeProjectForm();
            }
            else {
                this.formModel.formError = undefined;
                this.formSuccessMessage = "Draft saved";
                this.editingProjectHasDraft = true;
            }
            await this.reloadProjects();
        }
        catch {
            this.formModel.formError = publish ? "Unable to publish project" : "Unable to save draft";
        }
        finally {
            this.formBusy = false;
        }
    }
    async updateDraft(organizationId, projectId, publish, publishVersionName) {
        const result = await this.globalAdminProjectsManager.updateProjectDraft(organizationId, projectId, toUpdateStudyVersionInput(this.formModel.input, this.isPdda, publish, publishVersionName));
        if (isErr(result)) {
            this.formModel.formError = publish ? "Unable to publish project" : "Unable to save draft";
            return false;
        }
        return true;
    }
    validateInput(publish) {
        this.formModel.clearErrors();
        const { notBlank, validHttpsURL } = validators;
        const form = this.formModel;
        const httpsUrlErrorMessage = "Enter a valid HTTPS URL";
        form.validateField("title", notBlank, "Enter a project name");
        if (this.formMode === "create") {
            form.validateField("ownerId", notBlank, "Select a project owner");
        }
        if (this.isAIProject) {
            const maxAiToolNameLength = maxAiToolNameLengthForAiDataUseDefaults();
            form.validateField("aiToolName", notBlank, "Enter the AI tool name");
            form.validateField("aiToolName", (value) => value.length <= maxAiToolNameLength, "Shorten the AI tool name so default patient copy fits the character limit");
            form.validateField("aiChatbotUrl", notBlank, "Enter the chatbot URL");
            form.validateField("aiChatbotUrl", validHttpsURL, httpsUrlErrorMessage);
            form.validateField("aiSharingUrl", notBlank, "Enter the sharing URL");
            form.validateField("aiSharingUrl", validHttpsURL, httpsUrlErrorMessage);
            form.validateField("hipaaOrganizationLegalName", notBlank, "Enter the organization's legal name");
        }
        if (this.isPdda) {
            form.validateField("hipaaOrganizationLegalNameConfirmed", (confirmed) => confirmed, "Confirm the organization's legal name");
        }
        this.validateCatalogContactFields(httpsUrlErrorMessage);
        this.validateCatalogCharacterLimits();
        if (publish) {
            this.validateCatalogRequiredFields();
        }
        const firstErrorPage = firstValidationErrorPage(form.input, form.errors, PROJECT_BASICS_PAGE);
        if (firstErrorPage) {
            this.formPage = firstErrorPage;
        }
        if (form.hasErrors()) {
            this.formSuccessMessage = null;
        }
        return !form.hasErrors();
    }
    validateCatalogContactFields(httpsUrlErrorMessage) {
        const form = this.formModel;
        const { validEmail, validPhone, validHttpsURL } = validators;
        if (form.input.sitePrimaryContactEmail.trim() !== "") {
            form.validateField("sitePrimaryContactEmail", validEmail, "Enter a valid email address");
        }
        if (form.input.sitePrimaryContactPhone.trim() !== "") {
            form.validateField("sitePrimaryContactPhone", validPhone, "Enter a valid phone number");
        }
        if (form.input.sitePrimaryContactLink.trim() !== "") {
            form.validateField("sitePrimaryContactLink", validHttpsURL, httpsUrlErrorMessage);
        }
    }
    validateCatalogCharacterLimits() {
        const form = this.formModel;
        for (const page of this.catalogPages) {
            for (const section of page.sections) {
                for (const field of section.fields) {
                    if (!catalogFieldExceedsCharacterLimit(field, form.input)) {
                        continue;
                    }
                    form.formError = "Shorten text that is over the character limit";
                    if (field.fieldName && isStringFormField(field.fieldName, form.input)) {
                        form.addError(field.fieldName, "Shorten this to fit the character limit");
                    }
                }
            }
        }
    }
    validateCatalogRequiredFields() {
        const form = this.formModel;
        for (const page of this.catalogPages) {
            for (const section of page.sections) {
                for (const field of section.fields) {
                    if (!catalogFieldMissing(field, form.input)) {
                        continue;
                    }
                    form.formError = "Complete the required fields";
                    if (field.fieldName && isStringFormField(field.fieldName, form.input)) {
                        if (isContactMethodChoiceFieldName(field.fieldName)) {
                            if (field.fieldName === "sitePrimaryContactEmail") {
                                form.addError("sitePrimaryContactEmail", contactMethodRequiredMessage);
                            }
                            continue;
                        }
                        const overLimit = typeof field.characterLimit === "number" && form.input[field.fieldName].length > field.characterLimit;
                        if (overLimit) {
                            form.addError(field.fieldName, "Shorten this to fit the character limit");
                        }
                        else {
                            form.addError(field.fieldName, "This field is required");
                        }
                    }
                    else if (field.specialFieldName === "purposeOfDataDisclosure") {
                        form.addError("purposeOfDataDisclosure", "Select a purpose");
                    }
                    else if (field.specialFieldName === "healthRecordUpdates") {
                        form.addError("healthRecordsUpdatesEvery", "Enter an update interval");
                    }
                    else if (field.specialFieldName === "patientRecordRetrievalRecency") {
                        form.addError("patientRecordRetrievalRecencyEvery", "Enter a recency interval");
                    }
                    else if (field.specialFieldName === "additionalQuestions") {
                        form.addError("additionalQuestions", "Complete or remove the empty questions");
                    }
                    else if (field.specialFieldName === "organizationIcon") {
                        form.addError("organizationIconUrl", "This field is required");
                    }
                    else if (field.specialFieldName === "organizationRectangularIcon") {
                        form.addError("organizationRectangularIconUrl", "This field is required");
                    }
                }
            }
        }
    }
    targetOrganizationName() {
        const organizationId = this.globalAdminUIManager.organizationIdToAdmin;
        if (!organizationId) {
            return "";
        }
        const organization = this.organizationsManager.getOrganization(organizationId);
        if (organization === NOT_LOADED || organization === null) {
            return "";
        }
        return organization.name;
    }
    resetForm() {
        this.formModel.updateInput(emptyGlobalAdminProjectFormInput(this.targetOrganizationName()));
        this.formModel.clearErrors();
        this.formLoading = false;
        this.formBusy = false;
        this.formPage = PROJECT_BASICS_PAGE;
        this.editingProjectId = null;
        this.editingVersionName = null;
        this.editingProjectPublished = false;
        this.editingProjectHasDraft = false;
        this.formSuccessMessage = null;
        this.publishConfirmDialogOpen = false;
        this.publishConfirmed = false;
        this.publishVersionName = "";
    }
    applyDefaultOwnerIfNeeded() {
        var _a, _b;
        if (!this.formOpen || this.formMode !== "create") {
            return;
        }
        const ownerStillValid = this.projectOwners.some((owner) => owner.id === this.formModel.input.ownerId);
        if (ownerStillValid) {
            return;
        }
        this.formModel.updateInput({ ownerId: (_b = (_a = this.projectOwners[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "" });
    }
    async loadProjectOwners(organizationId) {
        const generation = ++this.ownersLoadGeneration;
        if (organizationId === null) {
            runInAction(() => {
                this.projectOwners = [];
                this.projectOwnersLoadFailed = false;
                this.applyDefaultOwnerIfNeeded();
            });
            return;
        }
        try {
            const owners = await this.globalAdminProjectsManager.listProjectOwners(organizationId);
            if (generation !== this.ownersLoadGeneration) {
                return;
            }
            runInAction(() => {
                this.projectOwners = owners;
                this.projectOwnersLoadFailed = false;
                this.applyDefaultOwnerIfNeeded();
            });
        }
        catch {
            if (generation !== this.ownersLoadGeneration) {
                return;
            }
            runInAction(() => {
                this.projectOwners = [];
                this.projectOwnersLoadFailed = true;
                this.applyDefaultOwnerIfNeeded();
            });
        }
    }
    async reloadProjects() {
        await this.loadProjects(this.globalAdminUIManager.organizationIdToAdmin);
    }
    async loadProjects(organizationId) {
        const generation = ++this.loadGeneration;
        runInAction(() => {
            this.projects = NOT_LOADED;
            this.projectsLoadFailed = false;
        });
        if (organizationId === null) {
            return;
        }
        try {
            const projects = await this.globalAdminProjectsManager.listProjects(organizationId);
            if (generation !== this.loadGeneration) {
                return;
            }
            runInAction(() => {
                this.projects = projects.filter((project) => project.projectType === ProjectType.AI_DATA_USE);
                this.projectsLoadFailed = false;
            });
        }
        catch {
            if (generation !== this.loadGeneration) {
                return;
            }
            runInAction(() => {
                this.projectsLoadFailed = true;
            });
        }
    }
}
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "publishConfirmDialogOpen", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "publishConfirmed", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "publishVersionName", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formOpen", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formPage", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "projects", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "projectOwners", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "projectOwnersLoadFailed", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "projectsLoadFailed", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "listError", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formMode", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "editingProjectId", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "editingVersionName", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "editingProjectPublished", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "editingProjectHasDraft", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formLoading", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formBusy", void 0);
__decorate([
    observable
], GlobalAdminProjectsViewModel.prototype, "formSuccessMessage", void 0);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "isAIProject", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "isPdda", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "editingProjectName", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "adminOrganizationId", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "catalogPages", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "publishVersionNameVisible", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "publishContinueEnabled", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "currentCatalogPage", null);
__decorate([
    computed
], GlobalAdminProjectsViewModel.prototype, "additionalQuestionLimitReached", null);
